import { Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo } from "react"

import { pluralize } from "../../../../utils/stringUtils"
import { getGoodsReceiptDeliveryLocation } from "../../utils"

import { PurchaseOrderSearchSuggestion } from "./PurchaseOrderSearchSuggestion"
import { PurchaseOrderSelectionChips } from "./PurchaseOrderSelectionChips"
import { PurchaseOrderSelectionTable } from "./PurchaseOrderSelectionTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { GoodsReceipt, Order } from "@/graphql/codegen/graphql"
import type { AutocompleteOption } from "@/types/form-fields"

interface PurchaseOrderSelectionDialogProps {
  searchQuery: string | AutocompleteOption
  inputValue: string
  purchaseOrders: Order[]
  isFetching: boolean
  selectedPurchaseOrders: Order[]
  goodsReceipt?: GoodsReceipt | null
  onSearch: (searchedItem: string | AutocompleteOption) => void
  onInputChange: (value: string) => void
  onSelect: (purchaseOrders: Order[]) => void
  onClose: () => void
  onContinue: (withPurchaseOrder: boolean) => void
  isStandalone?: boolean
}

export const PurchaseOrderSelectionDialog: FC<PurchaseOrderSelectionDialogProps> = ({
  searchQuery,
  inputValue,
  purchaseOrders,
  isFetching,
  selectedPurchaseOrders,
  goodsReceipt,
  onSearch,
  onInputChange,
  onSelect,
  onClose,
  onContinue,
  isStandalone = false,
}) => {
  const searchQueryDisplayValue =
    typeof searchQuery === "string" ? searchQuery : (searchQuery?.id as string) || searchQuery?.label || ""
  const purchaseOrdersCount = purchaseOrders.length ?? 0
  const hasNoPurchaseOrders = purchaseOrdersCount === 0 && !isFetching
  const canContinue = selectedPurchaseOrders.length > 0

  const handleRemove = (purchaseOrderToRemove: Order) => {
    const updatedSelection = selectedPurchaseOrders.filter(
      (purchaseOrder) => purchaseOrder.orderId !== purchaseOrderToRemove.orderId
    )
    onSelect(updatedSelection)
  }

  // Extract delivery location from goods receipt for validation
  const goodsReceiptDeliveryLocation = useMemo(() => getGoodsReceiptDeliveryLocation(goodsReceipt), [goodsReceipt])

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth='lg' data-testid='purchase-order-select-dialog'>
      <DialogHeader title={isStandalone ? "Add Purchase Orders" : "New Advanced Good Receipt"} onClose={onClose} />
      <DialogContent classes={{ root: "p-10" }}>
        <Typography className='mb-6 text-ml font-normal text-gray-900'>
          Search for a Purchase Order with same Ship-To locations to link shipments and ensure accurate tracking.
        </Typography>
        <Box className='flex flex-col gap-6'>
          <PurchaseOrderSearchSuggestion inputValue={inputValue} onSearch={onSearch} onInputChange={onInputChange} />
          {selectedPurchaseOrders.length > 0 && (
            <PurchaseOrderSelectionChips selectedPurchaseOrders={selectedPurchaseOrders} onRemove={handleRemove} />
          )}
          {hasNoPurchaseOrders && !isStandalone && (
            <InformationMissingAlert
              title='Start an AGR by manually add the shipping info and associate a PO later.'
              action={
                <Button
                  color='inherit'
                  size='small'
                  appearance='outlined'
                  className='text-primary'
                  startIcon={<AddOutlined />}
                  onClick={() => onContinue(false)}
                  data-testid='button-new-agr'
                >
                  New AGR
                </Button>
              }
            />
          )}
          {searchQuery && (
            <>
              <Typography className='inline-flex align-middle text-sm leading-6'>
                <span className='font-semibold text-gray-800'>
                  {purchaseOrdersCount} {pluralize(purchaseOrdersCount, "PO", "POs")} found
                </span>
                &nbsp;
                {Boolean(purchaseOrdersCount) && "Select one to add to the AGR"}
              </Typography>
              {hasNoPurchaseOrders ? (
                <Typography className='text-gray-500'>No POs found for "{searchQueryDisplayValue}"</Typography>
              ) : (
                <Box className='max-h-[400px] overflow-auto'>
                  <PurchaseOrderSelectionTable
                    purchaseOrders={purchaseOrders}
                    selectedPurchaseOrders={selectedPurchaseOrders}
                    goodsReceiptDeliveryLocation={goodsReceiptDeliveryLocation}
                    onSelect={onSelect}
                    isFetching={isFetching}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={onClose} data-testid='cancel-button'>
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={() => onContinue(true)}
          disabled={!canContinue}
          data-testid='continue-button'
        >
          {isStandalone ? "Confirm" : "Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
